import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import appApi from '../../../../api/app-api';
import dateFormat from '../../../../utils/date-format';
import Colors from '../../../../utils/colors';
import { toast } from 'react-toastify';
import { ReportLecturesType } from '../../../../api/types/reports/education/report-lectures.type';
import { useNavigate } from 'react-router-dom';
import '../home-lectures-widgets.css';

export const LecturesIssuesExceptionsHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportLecturesType[]>([]);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const defaultRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 1);
    return { start, end };
  }, []);

  const [startDate, setStartDate] = useState<Date | null>(defaultRange.start);
  const [endDate, setEndDate] = useState<Date | null>(defaultRange.end);
  const [thresholdMin, setThresholdMin] = useState<number>(5);
  const [thresholdMax, setThresholdMax] = useState<number | null>(null);

  const formatMinutesCompact = (minutes: number) => {
    const abs = Math.abs(minutes);
    const h = Math.floor(abs / 60);
    const m = Math.floor(abs % 60);
    if (h === 0)
      return `${new Intl.NumberFormat('he-IL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(abs)} דק׳`;
    return `${h}:${m.toString().padStart(2, '0')} ש׳`;
  };

  const ExceptionBadge = ({ minutes }: { minutes: number }) => {
    const isLate = minutes > 0;
    const bg = isLate ? '#FEE2E2' : '#DCFCE7';
    const fg = isLate ? Colors.dangerText : Colors.approveText2;
    const label = `${isLate ? '+' : '-'}${formatMinutesCompact(minutes)}`;

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 8px',
          borderRadius: '999px',
          background: bg,
          color: fg,
          fontSize: '0.85rem',
          fontWeight: 600,
          lineHeight: '18px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  };

  const fetchData = async () => {
    if (!startDate || !endDate) {
      toast.warn('יש לבחור טווח תאריכים מלא (מתאריך ועד תאריך)');
      return;
    }

    setLoading(true);
    try {
      const res = await appApi.getLecturesReport({
        branchesIds: [],
        abstractProgramIds: [],
        programsStartYears: [],
        lectureState: [],
        dateRangeStart: startDate,
        dateRangeEnd: endDate,
        exceptionThresholdMinMinutes: Math.max(0, thresholdMin ?? 0),
        exceptionThresholdMaxMinutes: thresholdMax,
        offset: 0,
        limit,
      });

      if (res.data.errCode) {
        toast.warn('לא ניתן לטעון נתונים');
        setData([]);
        setTotal(0);
        return;
      }

      const body: any = res.data.body;
      const rows = body?.data ?? [];
      setData(Array.isArray(rows) ? rows : []);
      setTotal(body?.total ?? 0);
    } catch (e) {
      toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="home-lecture-widget"
      style={{
        backgroundColor: '#ffffff',
        padding: '20px',
        height: '100%',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '10px',
          flexDirection: 'row',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            textAlign: 'right',
          }}
        >
          <div
            style={{
              color: '#185FD3',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexDirection: 'row-reverse',
              gap: '8px',
            }}
          >
            <span>מפגשים חורגים (דו״ח חריגות)</span>
            <i className="pi pi-exclamation-triangle" />
          </div>
          <div style={{ color: '#6B7280', fontSize: '13px' }}>
            מציג מפגשים עם חריגה בזמנים לפי סף דקות שנבחר. ניתן לשנות טווח
            תאריכים וסף חריגה, לרענן או לעבור לדו״ח המלא.
          </div>

          <div
            style={{
              marginTop: '8px',
              background: '#ECFDF5',
              border: '1px solid #A7F3D0',
              color: '#065F46',
              borderRadius: '10px',
              padding: '8px 10px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i className="pi pi-info-circle" style={{ marginTop: '2px' }} />
            <span>
              מוצגת כאן תצוגה מקדימה (עד {limit} רשומות). לדו״ח המלא עם כל
              הנתונים והסינונים יש ללחוץ על “לדו״ח המלא”.
            </span>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-start',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right',
                }}
              >
                מתאריך
              </span>
              <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{ width: '150px', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl' }}
              />
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right',
                }}
              >
                עד תאריך
              </span>
              <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{ width: '150px', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl' }}
                minDate={startDate || undefined}
              />
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right',
                }}
              >
                חריגה מינ׳ (דק׳)
              </span>
              <InputNumber
                value={thresholdMin}
                onValueChange={(e) => setThresholdMin((e.value ?? 0) as number)}
                min={0}
                style={{ maxWidth: '55px' }}
                inputStyle={{ maxWidth: '55px' }}
              />
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right',
                }}
              >
                חריגה מקס׳
              </span>
              <InputNumber
                value={thresholdMax}
                onValueChange={(e) =>
                  setThresholdMax((e.value ?? null) as number | null)
                }
                min={0}
                placeholder="ללא"
                style={{ width: '55px' }}
                inputStyle={{ maxWidth: '55px' }}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-start',
              whiteSpace: 'nowrap',
            }}
          >
            <Button
              icon="pi pi-refresh"
              label="רענן"
              size="small"
              onClick={() => {
                fetchData();
              }}
              loading={loading}
            />
            <Button
              icon="pi pi-external-link"
              label="לדו״ח המלא"
              size="small"
              tooltip={'לדו״ח המלא'}
              severity="secondary"
              outlined
              onClick={() => navigate('/reports/education/lectures')}
            />
          </div>
        </div>
      </div>

      <div style={{ fontSize: '13px', color: '#4b5563', textAlign: 'right' }}>
        סה״כ רשומות: {total.toLocaleString()}
      </div>

      <div style={{ marginTop: '10px', flex: 1 }}>
        <DataTable
          value={data}
          loading={loading}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          scrollHeight="280px"
          scrollable
          dataKey="courseId"
          tableStyle={{ direction: 'rtl' }}
          rowHover
        >
          <Column
            field="branchName"
            header="שלוחה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="abstractProgramName"
            header="תוכנית"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="courseName"
            header="קורס"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="additionalCourseName"
            header="שם קורס נוסף"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: ReportLecturesType) => row.additionalCourseName || '-'}
          />
          <Column
            header="תאריך"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: ReportLecturesType) =>
              row.scheduledStartTime
                ? dateFormat.displayFormatDate(row.scheduledStartTime)
                : '-'
            }
          />
          <Column
            header="חריגה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: ReportLecturesType) =>
              typeof row.minutesExceeded === 'number' ? (
                <ExceptionBadge minutes={row.minutesExceeded} />
              ) : (
                '-'
              )
            }
          />
          <Column
            header="מרצה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: ReportLecturesType) =>
              `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '-'
            }
          />
        </DataTable>
      </div>
    </div>
  );
};
